import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { User, Mail, Calendar, Shield, Save, Clock } from 'lucide-react';
import Button from '../Common/Button';
import ConfirmModal from '../Common/ConfirmModal';
import { apiService } from '../../services/apiService';
import { detectBrowserTimezone, getCurrentTime, COMMON_TIMEZONES } from '../../utils/timezoneUtils';

const SettingsSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionIcon = styled.div`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0 0 0;
  line-height: 1.5;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark.text};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.text};
  font-size: 0.875rem;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary[500]}20;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProfileImageSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ProfileImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const ProfileImageInfo = styled.div`
  flex: 1;
`;

const ProfileImageTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ProfileImageDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const DangerZone = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.accent.red};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.accent.red}05;
`;

const DangerTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent.red};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const DangerDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: 1.5;
`;

const AccountSettings = ({ onSettingsChange }) => {
  const { user, updateUserProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    timezone: detectBrowserTimezone(), // Auto-detect on load
    language: 'en'
  });
  const [currentTime, setCurrentTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get timezone list with current timezone at top
  const getTimezoneOptions = () => {
    const currentTz = formData.timezone;
    
    // Check if current timezone exists in the list
    const existsInList = COMMON_TIMEZONES.some(tz => tz.value === currentTz);
    
    // If current timezone is not in the list, add it at the top
    if (!existsInList && currentTz) {
      return [
        { value: currentTz, label: `${currentTz} (Current)` },
        ...COMMON_TIMEZONES
      ];
    }
    
    // Otherwise, move current timezone to top
    return [
      ...COMMON_TIMEZONES.filter(tz => tz.value === currentTz),
      ...COMMON_TIMEZONES.filter(tz => tz.value !== currentTz)
    ];
  };

  // Fetch user timezone from backend
  useEffect(() => {
    const fetchUserTimezone = async () => {
      if (!user) return;

      try {
        const response = await apiService.getUserTimezone();
        if (response.success) {
          setFormData(prev => ({
            ...prev,
            timezone: response.timezone
          }));
          console.log('✅ [SETTINGS] User timezone loaded:', response.timezone);
        }
      } catch (error) {
        console.warn('⚠️  [SETTINGS] Failed to fetch timezone (using auto-detected):', error);
        // Keep auto-detected timezone
      }
    };

    if (user) {
      setFormData(prev => ({
        ...prev,
        displayName: user.displayName || user.name || '',
        email: user.email || ''
      }));
      fetchUserTimezone();
    }
  }, [user]);

  // Update current time every second
  useEffect(() => {
    const updateCurrentTime = () => {
      const time = getCurrentTime(formData.timezone);
      setCurrentTime(time);
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 1000);

    return () => clearInterval(interval);
  }, [formData.timezone]);

  // Debug: Log timezone changes
  useEffect(() => {
    console.log('🌍 [SETTINGS] Current timezone in form:', formData.timezone);
  }, [formData.timezone]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    onSettingsChange(true);
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      // Update the user profile
      await updateUserProfile({
        displayName: formData.displayName,
        name: formData.displayName // Also update name field
      });

      // Update timezone (Phase 2A)
      await apiService.updateUserTimezone(formData.timezone);
      console.log('✅ [SETTINGS] Timezone saved:', formData.timezone);
      
      onSettingsChange(false); // Reset the unsaved changes flag
      
      // Show success toast
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('❌ [SETTINGS] Failed to update profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      // 1. Delete account from backend (will delete all user data)
      await apiService.deleteAccount();
      
      // Show success message before clearing storage
      toast.success('Account deleted successfully. Redirecting...');
      
      // 2. Clear all localStorage
      localStorage.clear();
      
      // 3. Clear all sessionStorage
      sessionStorage.clear();
      
      // 4. Sign out user
      await signOut();
      
      // 5. Navigate to home (login screen will show since user is null)
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
      
    } catch (error) {
      console.error('❌ Failed to delete account:', error);
      
      // Show styled error toast instead of browser alert
      toast.error('Failed to delete account. Please try again or contact support.', {
        duration: 5000,
      });
      
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Get avatar URL
  const avatarUrl = user?.photoURL || user?.avatar_url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <SettingsSection>
        <SectionHeader>
          <SectionIcon><User /></SectionIcon>
          <div>
            <SectionTitle>Profile Information</SectionTitle>
            <SectionDescription>
              Update your account details and personal information
            </SectionDescription>
          </div>
        </SectionHeader>

        <ProfileImageSection>
          <ProfileImage>
            {avatarUrl ? (
              <img 
                src={avatarUrl} 
                alt={user?.displayName || user?.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <User style={{ display: avatarUrl ? 'none' : 'flex' }} />
          </ProfileImage>
          <ProfileImageInfo>
            <ProfileImageTitle>Profile Picture</ProfileImageTitle>
            <ProfileImageDescription>
              Your profile picture is synced from your Google account
            </ProfileImageDescription>
          </ProfileImageInfo>
        </ProfileImageSection>

        <FormGrid style={{ marginTop: '1.5rem' }}>
          <FormGroup>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              placeholder="Enter your display name"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              disabled
              placeholder="Your email address"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="timezone">
              <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
              Timezone
            </Label>
            <Input
              id="timezone"
              as="select"
              value={formData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
            >
              {getTimezoneOptions().map((tz, index) => (
                <option key={tz.value} value={tz.value}>
                  {index === 0 ? `✓ ${tz.label}` : tz.label}
                </option>
              ))}
            </Input>
            {currentTime && (
              <small style={{ color: '#9ca3af', marginTop: '4px', fontSize: '0.75rem' }}>
                Current time: {currentTime}
              </small>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="language">Language</Label>
            <Input
              id="language"
              as="select"
              value={formData.language}
              onChange={(e) => handleInputChange('language', e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </Input>
          </FormGroup>
        </FormGrid>

        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            size="md"
            onClick={handleSaveProfile}
            loading={isSaving}
            disabled={isSaving}
          >
            <Save />
            Save Changes
          </Button>
        </div>
      </SettingsSection>

      <SettingsSection>
        <SectionHeader>
          <SectionIcon><Shield /></SectionIcon>
          <div>
            <SectionTitle>Account Security</SectionTitle>
            <SectionDescription>
              Manage your account security and data preferences
            </SectionDescription>
          </div>
        </SectionHeader>

        <DangerZone>
          <DangerTitle>Danger Zone</DangerTitle>
          <DangerDescription>
            Once you delete your account, there is no going back. All your data, conversations, 
            and memories will be permanently deleted.
          </DangerDescription>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteAccount}
          >
            Delete Account
          </Button>
        </DangerZone>
      </SettingsSection>

      {/* Delete Account Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Account"
        subtitle="This action cannot be undone"
        message="Are you sure you want to permanently delete your SARVIK account? All your data will be permanently removed, including: conversations, memories, reminders, voice profile, and all settings. This action cannot be reversed."
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingText="Deleting Account..."
      />
    </motion.div>
  );
};

export default AccountSettings;